import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { FiCheck, FiChevronRight, FiChevronDown, FiX, FiAlertCircle } from "react-icons/fi";
import { fetchWithAuth } from "../utils/authFetch";
import { creditsChanged } from "../utils/useCredits";

// Activities: long-running work the user started (imports, tracking, matching…), from /api/activity.
const POLL_BUSY_MS = 3000;

const ActivityContext = createContext(null);

/* One poller per tab, shared by the tray and the Dashboard. It asks /api/activity only while
   something is running (every POLL_BUSY_MS); idle it stays quiet and wakes on a job being queued
   (fetchWithAuth fires "activity:changed"), the tab becoming visible, or a route change. */
export function ActivityProvider({ children }) {
  const [jobs, setJobs] = useState([]);
  const [running, setRunning] = useState(0);
  const runningRef = useRef(0);
  const timerRef = useRef(null);
  const { pathname } = useLocation();

  const load = useCallback(async () => {
    try {
      const res = await fetchWithAuth("/api/activity");
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data?.jobs) ? data.jobs : [];
      setJobs(list);
      const nowRunning = Number(data?.running) || 0;
      // A run that just finished has charged credits: let the sidebar balance catch up.
      if (nowRunning < runningRef.current) creditsChanged();
      runningRef.current = nowRunning;
      setRunning(nowRunning);
    } catch (_) {
      // transient — next tick retries
    }
  }, []);

  useEffect(() => {
    let stopped = false;
    // Poll while jobs run; the chain ends by itself once nothing is running.
    const tick = async () => {
      await load();
      if (stopped) return;
      clearTimeout(timerRef.current);
      if (runningRef.current > 0) timerRef.current = setTimeout(tick, POLL_BUSY_MS);
    };
    const wake = () => {
      clearTimeout(timerRef.current);
      tick();
    };
    const onVisible = () => document.visibilityState === "visible" && wake();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("activity:changed", wake);
    wake();
    return () => {
      stopped = true;
      clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("activity:changed", wake);
    };
  }, [load]);

  // Opening another page is a natural moment to catch work started elsewhere.
  useEffect(() => {
    if (runningRef.current === 0) window.dispatchEvent(new Event("activity:changed"));
  }, [pathname]);

  const value = useMemo(() => ({ jobs, running, refresh: load }), [jobs, running, load]);
  return <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>;
}

export function useActivity() {
  const ctx = useContext(ActivityContext);
  if (!ctx) throw new Error("useActivity must be used within ActivityProvider");
  return ctx;
}

function relTime(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

function durationOf(job) {
  if (!job.startedAt || !job.finishedAt) return "";
  const s = Math.round((new Date(job.finishedAt) - new Date(job.startedAt)) / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

const slide = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;
const sweep = keyframes`
  from { left: -40%; }
  to { left: 100%; }
`;

const Bar = styled.div`
  height: 6px;
  border-radius: 4px;
  background: var(--line-soft);
  overflow: hidden;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: ${(p) => (p.$indeterminate ? "40%" : `${p.$pct || 0}%`)};
    border-radius: 4px;
    background: ${(p) =>
      p.$status === "failed" ? "var(--danger)" : p.$status === "succeeded" ? "var(--success)" : "var(--accent)"};
    transition: width 0.4s ease;
    animation: ${(p) => (p.$indeterminate ? sweep : "none")} 1.4s linear infinite;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 18px 1fr auto;
  gap: 10px;
  align-items: start;
  padding: 10px 0;
  border-bottom: 1px solid var(--line-soft);
  &:last-child {
    border-bottom: 0;
  }
  .t {
    font-size: 13px;
    font-weight: 600;
    color: var(--ink);
    line-height: 1.3;
  }
  .m {
    font-size: 12px;
    color: var(--ink-muted);
    margin-top: 2px;
    line-height: 1.35;
  }
  .when {
    font-size: 11.5px;
    color: var(--ink-muted);
    white-space: nowrap;
    padding-top: 2px;
  }
  .bar {
    margin-top: 7px;
  }
`;

const Dot = styled.span`
  display: inline-grid;
  place-items: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  margin-top: 1px;
  color: var(--on-fill, #fff);
  background: ${(p) =>
    p.$status === "failed" ? "var(--danger)" : p.$status === "succeeded" ? "var(--success)" : "var(--accent)"};
  ${(p) =>
    p.$status === "running" || p.$status === "queued"
      ? "background: transparent; border: 2px solid var(--accent); border-top-color: transparent; animation: spin 0.9s linear infinite;"
      : ""}
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Open = styled.button`
  border: 0;
  background: none;
  color: var(--accent);
  font: inherit;
  font-size: 12px;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

// The row's secondary actions (file toggle, open link) sit on one baseline, split by a hairline.
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
  .sep {
    width: 1px;
    height: 11px;
    background: var(--line-strong);
  }
`;

const Items = styled.div`
  margin-top: 6px;
  border-left: 2px solid var(--line-soft);
  padding-left: 8px;
  .it {
    display: grid;
    grid-template-columns: 12px 1fr;
    gap: 6px;
    align-items: start;
    font-size: 12px;
    color: var(--ink-muted);
    padding: 3px 0;
    line-height: 1.3;
  }
  .it .n {
    color: var(--ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .it .s {
    font-size: 11.5px;
  }
`;
const ItemDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 4px;
  background: ${(p) =>
    p.$status === "failed" ? "var(--danger)" : p.$status === "succeeded" ? "var(--success)" : "var(--accent)"};
  ${(p) => (p.$status === "queued" ? "opacity: 0.35;" : "")}
`;

// One job, shared by the tray and the Dashboard card. A batched upload carries its
// files as `items`, shown behind a toggle so the list stays one row per upload.
export function ActivityRow({ job, showWhen = true }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = Array.isArray(job.items) ? job.items : [];
  const active = job.status === "running" || job.status === "queued";
  const pct = job.progress && job.progress.total > 0 ? Math.round((100 * job.progress.done) / job.progress.total) : null;
  const detail = active
    ? [job.status === "queued" ? "Waiting to start" : job.stage, pct != null && job.progress ? `${job.progress.done} / ${job.progress.total}` : null]
        .filter(Boolean)
        .join(" · ")
    : job.message;
  return (
    <Row>
      <Dot $status={job.status}>
        {job.status === "succeeded" && <FiCheck size={11} />}
        {job.status === "failed" && <FiAlertCircle size={11} />}
      </Dot>
      <div style={{ minWidth: 0 }}>
        <div className="t">{job.label}</div>
        {detail && <div className="m">{detail}</div>}
        {active && (
          <div className="bar">
            <Bar $status={job.status} $pct={pct ?? 0} $indeterminate={pct == null || job.status === "queued"} />
          </div>
        )}
        {(items.length > 0 || (!active && job.link)) && (
          <Actions>
            {items.length > 0 && (
              <Open onClick={() => setOpen((v) => !v)} aria-expanded={open}>
                {open ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                {items.length} {items.length === 1 ? "file" : "files"}
              </Open>
            )}
            {items.length > 0 && !active && job.link && <span className="sep" aria-hidden="true" />}
            {!active && job.link && (
              <Open onClick={() => navigate(job.link)}>
                Open <FiChevronRight size={12} />
              </Open>
            )}
          </Actions>
        )}
        {open && items.length > 0 && (
          <Items>
            {items.map((it) => (
              <div className="it" key={it.id}>
                <ItemDot $status={it.status} />
                <div style={{ minWidth: 0 }}>
                  <div className="n" title={it.label}>{it.label}</div>
                  {it.message && <div className="s">{it.message}</div>}
                </div>
              </div>
            ))}
          </Items>
        )}
      </div>
      {showWhen && (
        <div className="when">
          {active ? relTime(job.startedAt || job.createdAt) : `${relTime(job.finishedAt)}${durationOf(job) ? ` · ${durationOf(job)}` : ""}`}
        </div>
      )}
    </Row>
  );
}

const TrayWrap = styled.div`
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 89;
  width: 320px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 12px 14px 8px;
  border-radius: 12px;
  background: var(--surface);
  border: 1px solid var(--line);
  box-shadow: 0 10px 32px var(--scrim), var(--shadow-md);
  animation: ${slide} 200ms ease-out;
  color: var(--ink);

  /* A fixed 320px card at 20px insets fills a 360px phone edge to edge with a lopsided gutter. */
  @media (max-width: 450px) {
    left: 12px;
    right: 12px;
    bottom: 12px;
    width: auto;
    max-height: 50vh;
  }
`;
const TrayHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
  button {
    border: 0;
    background: none;
    color: var(--ink-muted);
    cursor: pointer;
    padding: 2px;
    display: grid;
    place-items: center;
  }
`;

// Recently finished jobs stay visible this long after finishing.
const RECENT_MS = 3 * 60 * 1000;

const decodePath = (p) => {
  try {
    return decodeURIComponent(p || "");
  } catch {
    return p || "";
  }
};

// True when the current page already shows this job's progress itself, so the tray would only repeat it.
function shownByPage(job, pathname) {
  // Find Candidates has its running sheet and run dock on every one of its screens.
  if (job.kind === "find_candidates") return /^\/find-candidates(\/|$)/.test(pathname);
  // A company's Research page carries its own "Building profile…" / "Mapping HR contacts…" state.
  if (job.kind === "profile" || job.kind === "find_hr")
    return !!job.link && decodePath(job.link) === decodePath(pathname);
  return false;
}

export default function ActivityTray() {
  const { jobs } = useActivity();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(() => new Set());
  const onDashboard = pathname === "/";
  const shown = jobs.filter((j) => {
    if (dismissed.has(j.id)) return false;
    if (shownByPage(j, pathname)) return false;
    if (j.status === "running" || j.status === "queued") return true;
    return j.finishedAt && Date.now() - new Date(j.finishedAt).getTime() < RECENT_MS;
  });
  if (onDashboard || shown.length === 0) return null;
  return (
    <TrayWrap role="status" aria-live="polite">
      <TrayHead>
        <span>Activities</span>
        {/* Hides everything listed, running jobs included — they keep running; a newly started job brings the tray back. */}
        <button title="Hide" aria-label="Hide activities" onClick={() => setDismissed((d) => new Set([...d, ...shown.map((j) => j.id)]))}>
          <FiX size={14} />
        </button>
      </TrayHead>
      {shown.map((j) => (
        <ActivityRow key={j.id} job={j} showWhen={false} />
      ))}
    </TrayWrap>
  );
}
